import React, { ChangeEvent, FC, useState, SyntheticEvent } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { useFeatures } from 'flagged';

import { getGame, getGameAirings, getGameFers } from './apiQueries';
import { createOrUpdateGame } from './apiMutations';
import GameDetails from './GameDetails';
import GameDetailsEditor from './GameDetailsEditor';
import EditorForm from '../common/EditorForm';
import { DateChangeEvent } from './GameDetailsEditor';
import EditorialHighlights from '../common/EditorialHighlights';
import { convertGameToGameInput } from '../../utils/inputTransform';
import { Airing, Game, OffsetConnection, USE_DRM } from '../../utils/types';
import { useAlertContext } from '../../context/alertContext';
import Exclusions from '../exclusions/Exclusions';

const AUTO_REFETCH_PERIOD = 15 * 1000; // 15 seconds in milliseconds
const NO_REFETCH = 0;

const GameComp: FC<GameProps> = (props) => {
	const { id }: GameParams = useParams();
	const history = useHistory();
	const { setAlert } = useAlertContext();
	const { drmMode: drmModeFlag } = useFeatures();
	const drmMode = drmModeFlag?.toString();
	const { edit } = props;

	const [autoRefetch, setAutoRefetch] = useState<boolean>(true);
	const [updatedSport, setUpdatedSport] = useState('');
	const [updatedStartTime, setUpdatedStartTime] = useState('');
	const [updatedTitle, setUpdatedTitle] = useState('');
	const [saveDisabled, setSaveDisabled] = useState(true);
	const [airingsPage, setAiringsPage] = useState(0);
	const [airingsPageSize, setAiringsPageSize] = useState(5);
	const [updatedOffsets, setUpdatedOffsets] = useState<OffsetConnection>({ items: [] });

	const useDrm = drmMode === 'yes' ? USE_DRM.YES : drmMode === 'both' ? USE_DRM.BOTH : USE_DRM.NO;

	const { loading, error, data } = useQuery(getGame, {
		variables: { id: decodeURIComponent(id) }
	});

	const {
		loading: airingsLoading,
		error: airingsError,
		data: dataAirings,
		fetchMore: fetchMoreAirings,
		refetch: refetchAirings
	} = useQuery(getGameAirings, {
		variables: {
			id: decodeURIComponent(id),
			limit: airingsPageSize * 2,
			nextToken: null
		},
		pollInterval: autoRefetch ? AUTO_REFETCH_PERIOD : NO_REFETCH
	});

	const {
		loading: fersLoading,
		error: fersError,
		data: dataFers,
		refetch: refetchFers
	} = useQuery(getGameFers, {
		variables: {
			id: decodeURIComponent(id)
		}
	});

	const [updateGame] = useMutation(createOrUpdateGame);
	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data.getGame) return <Typography>No game found</Typography>;

	const handleChange = (event: ChangeEvent<HTMLInputElement> | DateChangeEvent): void => {
		const { name, value } = event.target;
		switch (name) {
			case 'sport':
				setUpdatedSport(value);
				break;
			case 'startTime':
				setUpdatedStartTime(value);
				break;
			case 'title':
				setUpdatedTitle(value);
				break;
		}
		setSaveDisabled(false);
	};

	const handleOffsetsChange = (offsets: OffsetConnection): void => {
		setUpdatedOffsets(offsets);
		setSaveDisabled(false);
	};

	const handleLoadMoreAirings = (): void => {
		const { nextToken } = dataAirings?.getGame?.airings;
		if (nextToken) {
			fetchMoreAirings({
				query: getGameAirings,
				variables: {
					id: decodeURIComponent(id),
					limit: airingsPageSize,
					nextToken: nextToken
				},
				updateQuery: (previousResult: listGameAirings, { fetchMoreResult }): listGameAirings => {
					if (!fetchMoreResult) return previousResult;
					const newNextToken = fetchMoreResult.getGame.airings.nextToken;
					const items = [...previousResult.getGame.airings.items, ...fetchMoreResult.getGame.airings.items];
					return {
						getGame: {
							id: id,
							__typename: 'Game',
							airings: {
								items: items,
								nextToken: newNextToken,
								__typename: 'Airing'
							}
						}
					};
				}
			});
		}
	};

	const handleAiringsPageChange = (page: number): void => {
		handleLoadMoreAirings();
		setAiringsPage(page);
	};
	const handleAiringsPageSizeChange = (pageSize: number): void => {
		setAiringsPageSize(pageSize);
		setAiringsPage(0);
		refetchAirings({
			id: decodeURIComponent(id),
			limit: pageSize * 2,
			nextToken: null
		});
	};
	const handleGameFormCancel = (event: SyntheticEvent): void => {
		history.push(`/games/${id}`);
	};

	const handleGameFormSubmit = async (event: SyntheticEvent): Promise<unknown> => {
		event.stopPropagation();
		event.preventDefault();

		const game: Game = {
			id,
			...(updatedSport && { sport: updatedSport }),
			...(updatedStartTime && { startTime: updatedStartTime }),
			...(updatedTitle && { title: updatedTitle }),
			...(updatedOffsets?.items?.length && updatedOffsets?.items?.length > 0
				? { offsets: updatedOffsets }
				: updatedOffsets.items == null
					? { offsets: null }
					: { offsets: offsets })
		};
		const gameInput = convertGameToGameInput(game);

		if (window.confirm('Are you sure you want to update the game? This cannot be undone.')) {
			setSaveDisabled(true);
			try {
				await updateGame({ variables: { game: gameInput } });
				setAlert('Game updated');
				history.push(`/games/${id}`);
			} catch {
				setAlert('Error updating game');
				setSaveDisabled(false);
			}
		}
		return true;
	};

	const handleFerReload = (): void => {
		refetchFers({
			id: decodeURIComponent(id)
		});
	};

	const { merlinId, sport, startTime, teams, title, status, editorialHighlight, offsets, league } = data.getGame;
	const airings = dataAirings?.getGame?.airings;
	const fers = dataFers?.listFers?.items?.filter(({ __typename: t }: { __typename: string }): boolean => t === 'Fer');

	const getView = (edit?: string): JSX.Element => {
		switch (edit) {
			case 'edit':
				return (
					<EditorForm
						cancelText='Cancel'
						onCancel={handleGameFormCancel}
						onSubmit={handleGameFormSubmit}
						submitText='Update Game'
						saveDisabled={saveDisabled}>
						<GameDetailsEditor
							id={id}
							sport={updatedSport || sport}
							startTime={updatedStartTime || startTime}
							title={updatedTitle || title}
							offsets={
								updatedOffsets?.items?.length && updatedOffsets?.items?.length > 0 ? updatedOffsets : updatedOffsets.items == null ? null : offsets
							}
							onChange={handleChange}
							onOffsetsChange={handleOffsetsChange}
						/>
					</EditorForm>
				);
			case 'editorialHighlight':
				return airings?.items ? (
					<EditorialHighlights
						highlightType='game'
						airings={airings.items}
						game={{
							id: id,
							sport: sport,
							startTime: startTime,
							teams: teams,
							title: title,
							league: league
						}}
						editorialHighlight={editorialHighlight}
						onAlert={setAlert}
					/>
				) : (
					<Typography>Loading...</Typography>
				);
			case 'exclusions':
				return <Exclusions id={id} channel={undefined} pageType={'game'} game={data.getGame} status={status}  />;
			default:
				return (
					<GameDetails
						airings={airings}
						airingsPaginationOptions={{
							loading: airingsLoading,
							error: airingsError,
							pageSize: airingsPageSize,
							page: airingsPage,
							pageOptions: [5, 10, 15],
							handlePageSizeChange: handleAiringsPageSizeChange,
							handlePageChange: handleAiringsPageChange
						}}
						fers={fers}
						fersLoadingOptions={{
							loading: fersLoading,
							error: fersError,
							handleReload: handleFerReload
						}}
						id={id}
						merlinId={merlinId}
						sport={sport}
						startTime={startTime}
						teams={teams}
						title={title}
						gameStatus={status}
						offsets={offsets}
						league={league}
						useDrm={useDrm}
						setAutoRefetch={setAutoRefetch}
					/>
				);
		}
	};

	return getView(edit);
};

type GameParams = { id: string };

type GameProps = { edit?: string };

type listGameAirings = {
	getGame: {
		id: string;
		__typename: 'Game';
		airings: {
			items: Airing[];
			nextToken: string;
			__typename: 'Airing';
		};
	};
};

export default GameComp;
